import { useCallback, useEffect, useRef } from 'react';

/**
 * For a loader that can be called again before its previous call settled: React.StrictMode
 * runs a mount effect twice, and pages re-fetch after their own writes or when the record
 * being shown changes. Call the returned `beginRequest()` as a load starts; the
 * `isCurrent()` it hands back turns false as soon as a newer load begins, so a late
 * response is dropped instead of overwriting what the user has typed since.
 *
 * Unmounting drops everything too, including a request a leftover event handler begins
 * afterwards, so a late answer never toasts or navigates for a page the user has left.
 * StrictMode's simulated unmount only invalidates the requests of its first mount run,
 * which is what the second run replaces anyway.
 *
 * Effects whose fetch nothing else calls use a `cancelled` flag in their cleanup instead
 * (see EditSalary.js); this is for loaders shared with event handlers. A page whose record
 * comes from the URL also remounts per record (utils/remountOnId.js), so a handler still
 * running for the previous record cannot reach the new one's state.
 *
 * @returns {() => () => boolean} beginRequest
 */
export default function useLatestRequest() {
  const latest = useRef(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      latest.current += 1;
    };
  }, []);

  return useCallback(() => {
    if (!mounted.current) return () => false;
    latest.current += 1;
    const mine = latest.current;
    return () => mounted.current && mine === latest.current;
  }, []);
}
